import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from "@angular/material/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { ViewContentPageComponent } from "@vp/content-management/ui/view-content-page";
import { ContentFilterState } from "@vp/data-access/content-management";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import { defaultJsonschemaOptions } from "@vp/formly/json-schema";
import {
  UiSchemaConfigService,
  UiSchemaLayoutProvider,
  UiSchemaStateProvider
} from "@vp/formly/ui-schema-config";
import {
  ContentData,
  Department,
  Organization,
  OrganizationFeatures,
  Role,
  Tag,
  TagFilter,
  User
} from "@vp/models";
import { AccessControlService } from "@vp/shared/access-control";
import { FeatureService } from "@vp/shared/features";
import { LocaleService } from "@vp/shared/locale-service";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import { PermissionsConstService } from "@vp/shared/permissions-const";
import { AppStoreService } from "@vp/shared/store/app";
import { UiDisplayTagService } from "@vp/shared/store/ui";
import { RouterUtilities, deeperCopy, mergeDeep } from "@vp/shared/utilities";
import {
  RolesAssignmentService,
  UserAdministrationService,
  UserAdministrationState
} from "@vp/user-administration/data-access/user-administration-state";
import moment from "moment";
import { NgxPermissionsService } from "ngx-permissions";
import { Operation } from "rfc6902";
import { BehaviorSubject, Observable, Subject, combineLatest, from, of, zip } from "rxjs";
import {
  concatMap,
  filter,
  first,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  withLatestFrom
} from "rxjs/operators";

export interface IWarningReason {
  title: string;
  reason: string;
}

@Component({
  selector: "vp-user-profile-shell",
  standalone: false,
  templateUrl: "./user-profile-shell.component.html",
  styleUrls: ["./user-profile-shell.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    RolesAssignmentService,
    UserAdministrationService,
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (localeService: LocaleService) => {
        return localeService.getLocale();
      },
      deps: [LocaleService]
    },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (localeService: LocaleService) => {
        return localeService.getDateFormat();
      },
      deps: [LocaleService]
    }
  ]
})
export class UserProfileShellComponent implements OnInit, AfterViewInit, OnDestroy {
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  contents$: Observable<ContentData[]> = inject(Store).select(ContentFilterState.allContents);
  workingCopy$: Observable<User | null> = inject(Store).select(UserAdministrationState.workingCopy);
  pendingOperations$: Observable<Operation[]> = inject(Store).select(
    UserAdministrationState.pendingOperations
  );
  filterTags$: Observable<Tag[]> = inject(Store).select(TagsState.filtered);

  dateFormat = "";
  optIn!: boolean;

  //  TODO: messages needs to be consolidated into configurationlists
  optInVerbiage = this.featureService.messages$(OrganizationFeatures.smsOptIn).pipe(
    map(messages => {
      const defaultOptInMsg = "Opt-in SMS notification";
      if (messages) {
        return messages["optInMessage"] ?? defaultOptInMsg;
      }
      return defaultOptInMsg;
    })
  );

  private _destroyed$ = new Subject<void>();
  private _optIn$ = new BehaviorSubject<boolean>(false);

  form: UntypedFormGroup = new UntypedFormGroup({});
  fields: FormlyFieldConfig[] = [];
  model = {};
  options: FormlyFormOptions = {};
  selectedTags: string[] = [];

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly store: Store,
    private readonly accessControlService: AccessControlService,
    private readonly appStoreService: AppStoreService,
    private readonly featureService: FeatureService,
    private readonly localeService: LocaleService,
    private readonly notificationService: NotificationService,
    private readonly ngxPermissionsService: NgxPermissionsService,
    private readonly routerUtilitiesService: RouterUtilities,
    private readonly uiSchemaConfigService: UiSchemaConfigService,
    private readonly uiSchemaLayoutProvider: UiSchemaLayoutProvider,
    private readonly uiSchemaStateProvider: UiSchemaStateProvider,
    private readonly userAdministrationService: UserAdministrationService,
    public readonly permConst: PermissionsConstService,
    public readonly uiDisplayTagService: UiDisplayTagService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  user$ = this.appStoreService.user$.pipe(take(1));
  optIn$ = this._optIn$.asObservable();

  hasPending$ = this.pendingOperations$.pipe(map(operations => operations.length > 0));

  featureLayout$ = this.featureService
    .featureLayouts$(OrganizationFeatures.profile)
    .pipe(filterNullMap())
    .subscribe(layout => this.uiSchemaConfigService.addScopedConfig(layout, `edit-user-profile`));

  imported$ = this.user$.pipe(
    map(user => user.tags),
    map(tags => tags.includes("import")),
    takeUntil(this._destroyed$)
  );

  firstLogin$ = this.user$.pipe(
    shareReplay(1),
    map(user => user.tags),
    map(tags => {
      return !tags.includes("profile.complete");
    }),
    takeUntil(this._destroyed$)
  );

  showRolesAndGroups$ = this.ngxPermissionsService.permissions$.pipe(
    filterNullMap(),
    mergeMap(() => {
      return from(
        this.ngxPermissionsService.hasPermission([this.permConst.Profile.DeptmentAndGroup.Read])
      );
    })
  );

  reason$ = combineLatest([this.firstLogin$, this.imported$]).pipe(
    map(([firstLogin]) => {
      if (firstLogin) {
        return {
          type: "accent",
          title: "Thank you for registering!",
          reason:
            "Some details are missing from your profile. Please add any additional information and click save or close to continue."
        } as IWarningReason;
      }
      return null;
    }),
    takeUntil(this._destroyed$)
  );

  defaultToSLanguageTag$ = this.filterTags$.pipe(
    filter(tags => tags.length > 0),
    take(1),
    withLatestFrom(
      this.featureService.configurationValue$(
        OrganizationFeatures.termsOfService,
        "defaultToSLanguage"
      )
    ),
    map(([tags, defaultLanguage]) => {
      return tags.find(t => t.displayName === defaultLanguage);
    }),
    filterNullMap()
  );

  ngOnInit(): void {
    this.dateFormat = this.localeService.getLocaleDateFormat();

    this.user$.pipe(take(1)).subscribe((user: User) => {
      this.userAdministrationService.loadUser(user);
      this.model = deeperCopy(user);
    });

    this.user$
      .pipe(
        concatMap(user =>
          zip(
            of(user),
            this.firstLogin$,
            this.featureService.featureEnabled$(OrganizationFeatures.smsOptIn),
            this.featureService
              .configurationLists$(OrganizationFeatures.smsOptIn)
              .pipe(map(configs => (configs ? configs["defaultOptInDepartments"] : null)))
          )
        ),
        takeUntil(this._destroyed$)
      )
      .subscribe(([user, firstLogin, smsFeatureEnabled, defaultOptInDepartments]) => {
        this._optIn$.next(user.tags.includes("sms-opt-in"));
        if (smsFeatureEnabled && defaultOptInDepartments) {
          if (firstLogin) {
            // on first login, default sms opt in to true if user belongs to one of the default opt in departments
            this._optIn$.next(this.checkForAutoOptIn(user, defaultOptInDepartments));
          }
        }
      });

    this.user$
      .pipe(
        map(user => deeperCopy(user)),
        takeUntil(this._destroyed$)
      )
      .subscribe((user: User) => {
        if (user.profile.dateOfBirth) {
          /* I hate this but I cant think of another way around this, the moment
           * date adapter is turning this into a moment object which throws an
           * error so this just converts it back to a js date.
           */
          this.form.patchValue(
            {
              profile: {
                dateOfBirth: moment(user.profile.dateOfBirth).toDate()
              }
            },
            { emitEvent: false }
          );
        }
      });
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((user: Partial<User>) => {
      this.userAdministrationService.updateWorkingCopy(user);
    });

    combineLatest([
      this.featureService.configurationLists$(OrganizationFeatures.profile),
      this.appStoreService.userDepartments$
    ])
      .pipe(
        map(([configLists, depts]: [Record<string, string[]>, Department[]]) =>
          this.showViewSource(depts, configLists)
            ? "editProfileComponentWithSource"
            : "editProfileComponent"
        ),
        switchMap(componentName => {
          return this.featureService
            .featureSchema$(OrganizationFeatures.profile, componentName)
            .pipe(
              switchMap((schema: Record<string, unknown>) => {
                return this.uiSchemaLayoutProvider.applyScopes(
                  componentName,
                  schema,
                  `edit-user-profile`
                );
              })
            );
        }),
        filterNullMap(),
        concatMap(schema => {
          return combineLatest([
            of(this.uiSchemaLayoutProvider.getFieldConfig(schema, defaultJsonschemaOptions)),
            this.uiSchemaStateProvider.applyScopes(
              "editProfileComponent",
              { ...this.options },
              "edit-user-profile"
            ),
            this.getTagFilters$()
          ]);
        }),
        take(1)
      )
      .subscribe(
        ([fieldConfigs, options, tagFilters]: [FormlyFieldConfig[], FormlyFormOptions, Tag[]]) => {
          this.fields = fieldConfigs;
          this.options = options;
          this.options.formState.selectedTags = tagFilters;
          this.options.formState.tagGroups = [
            ...this.store.selectSnapshot(TagsState.filteredGroups)
          ];
          this.cdr.markForCheck();
        }
      );
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  getTagFilters$(): Observable<Tag[]> {
    return this.appStoreService.userDataSlice$<TagFilter[]>("tagFilters").pipe(
      map(
        (tagFilters: TagFilter[] | null) =>
          tagFilters?.map((tagFilter: TagFilter) => tagFilter.tagId) ?? []
      ),
      withLatestFrom(this.filterTags$),
      map(([selectedTags, tags]: [string[], Tag[]]) => {
        return tags.filter((tag: Tag) => selectedTags.includes(tag.tagId));
      }),
      shareReplay(1)
    );
  }

  onSave = () => {
    if (!this.form.valid || !this.form.value) return;

    combineLatest([this.optIn$, this.workingCopy$.pipe(filterNullMap())])
      .pipe(
        take(1),
        switchMap(([smsOptIn, user]: [boolean, User]) => {
          const modified = mergeDeep(user, this.form.value, "replace");
          if (!modified.tags.includes("profile.complete")) {
            modified.tags.push("profile.complete");
          }
          if (smsOptIn) {
            if (!modified.tags.includes("sms-opt-in")) {
              modified.tags.push("sms-opt-in");
            }
          } else {
            modified.tags = modified.tags.filter((t: string) => t !== "sms-opt-in");
          }
          return this.appStoreService.patchUser(modified, "user_saveProfile");
        }),
        mergeMap(() => this.onSaveNavigation())
      )
      .subscribe({
        next: (success: boolean) => {
          if (success) {
            this.notificationService.successMessage("Profile saved");
          } else {
            this.notificationService.errorMessage(
              "There was a problem saving your profile information."
            );
          }
        }
      });
  };

  tosInformation() {
    this.contents$
      .pipe(
        take(1),
        withLatestFrom(this.user$, this.defaultToSLanguageTag$),
        switchMap(([contents, user, defaultLanguageTag]: [ContentData[], User, Tag]) => {
          const defaultToS = contents.find(
            c => c.contentTypeId === "terms-of-service" && c.tags.includes(defaultLanguageTag.tagId)
          );
          const languageTagId = user.profile.language ?? defaultLanguageTag.tagId;

          const toS = contents.find(
            (contentData: ContentData) =>
              contentData.contentTypeId === "terms-of-service" &&
              contentData.tags.includes(languageTagId)
          );

          const tosContentId = toS?.contentId ?? defaultToS?.contentId;

          const dialogRef = this.dialog.open(ViewContentPageComponent, {
            autoFocus: false,
            width: "80vw",
            data: {
              contentId: tosContentId
            },
            panelClass: "mobile-responsive"
          });
          return dialogRef.afterClosed();
        })
      )
      .subscribe();
  }

  onSaveNavigation = () => {
    return this.firstLogin$.pipe(
      withLatestFrom(
        this.organization$.pipe(
          filterNullMap(),
          map(org => org.autoCreateInitialCaseRoles ?? [])
        ),
        this.appStoreService.selectedRole
      ),
      switchMap(([firstLogin, autoCreateRoles, selectedRole]: [boolean, string[], Role]) => {
        if (firstLogin) {
          return this.accessControlService.userSelectedRoleIncludes(autoCreateRoles).pipe(
            switchMap(hasRoles => {
              if (hasRoles) {
                return this.router.navigate(["wizard"]);
              }
              const { path, queryParams } =
                this.routerUtilitiesService.getRouteDefaultFromRole(selectedRole);
              return this.router.navigate([path], { queryParams });
            })
          );
        } else {
          this.returnToPreviousRoute();
        }
        return of(true);
      }),
      first()
    );
  };

  toggleOptIn(event: boolean) {
    this._optIn$.next(event);
  }

  checkForAutoOptIn(user: User, defaultOptInDepartments: string[]) {
    return user.roles.some(r => {
      for (const department of r.departments) {
        if (department.friendlyId) {
          return defaultOptInDepartments?.includes(department.friendlyId);
        }
      }
      return false;
    });
  }

  showViewSource(depts: Department[], configLists: Record<string, string[]>): boolean {
    if (configLists) {
      const viewSourceDepts = configLists["viewSourceDepts"];
      if (viewSourceDepts?.length > 0) {
        //if a user belongs to multiple depts, use the default
        const userDept = depts.length < 2 ? depts.find(d => d) : depts.find(d => d.isDefault);
        if (userDept?.friendlyId) {
          return viewSourceDepts?.includes(userDept.friendlyId) ? true : false;
        }
      }
    }
    return false;
  }

  returnToPreviousRoute() {
    this.activatedRoute.queryParams.pipe(take(1)).subscribe((query: Params) => {
      const returnUrl = query["returnUrl"];
      if (returnUrl) {
        this.router.navigateByUrl(returnUrl);
      } else {
        this.router.navigateByUrl("/");
      }
    });
  }
}
